import { useCallback, useEffect, useState, type DragEvent, type MouseEvent, type ReactNode } from "react";
import { useT } from "../i18n";
import { FehlerBand, Knopf } from "./index";
import { Zeichen } from "./Zeichen";
import { useZuteilen } from "../daten/faelle";
import { Fehler } from "../daten/klient";
import type { Bandeintrag, Verteilperson, Verteilstand, Zuteilung } from "../daten/faelle";
import {
  ZURUECK, hindernis, istBeweglich, kuerzelVon, lageAm, stufeVon, uhrzeit, zuteilungenFuer,
  type Aufgabe, type Hindernis,
} from "../daten/verteilen";
import "../stile/aufgaben.css";

/* ══════════════════════════════════════════════════════════════════
   Verteilen — die Bausteine, die Brett und Telefon teilen
   (25.09.2026, 53-verteilen.md, Entscheidung Luca: Variante 1).

   Eingeteilt wird auf der Seite Einteilen (`masken/disposition/Einteilen.tsx`)
   und am Telefon auf der Startseite (`masken/handy/Zuverteilen.tsx`). Beide
   zeigen dieselbe **Marke** je Aufgabe, dieselbe **Leuteliste** im Blatt
   und schreiben über denselben Weg (`POST /aufgaben/zuteilen`) — mit
   „Rückgängig" für ein paar Sekunden.

   **Die Farbe sagt die Art, der Rand den Stand.** Vorbereiten blau,
   Ausliefern/Übergeben grün, Zurückholen/Zurücknehmen ocker; gestrichelt
   rot heißt *noch niemand*. Ein Hinweis (Urlaub, Einsatz zur selben Zeit,
   fehlende Qualifikation) steht als Wort an der Marke — Farbe ist nie der
   einzige Träger (28-designkonzept.md).
   ══════════════════════════════════════════════════════════════════ */

export type Tr = ReturnType<typeof useT>;

/** Die Farbklasse einer Aufgabe: Vorbereiten, hinaus, herein. */
export function artKlasse(a: Pick<Aufgabe, "art">): "vorb" | "aus" | "zur" {
  return a.art === "vorbereitung" ? "vorb" : a.art === "uebergabe" ? "aus" : "zur";
}

/** Das Wort der Aufgabe — „Ausliefern" oder „Übergeben", je nachdem, wer fährt. */
export function aufgabenwort(a: Pick<Aufgabe, "art" | "amHof">, t: Tr): string {
  if (a.art === "vorbereitung") return t("verteilen.aufgabe.vorbereitung");
  return t(`verteilen.aufgabe.${a.art}${a.amHof ? "Hof" : "Weg"}`);
}

export function hindernisText(h: Hindernis, stand: Pick<Verteilstand, "qualifikationen">, t: Tr): string {
  switch (h.art) {
    case "abwesend": return t(`verteilen.band.${h.grund}`);
    case "einsatz": return h.kennung
      ? t("verteilen.hindernis.einsatz", { kennung: h.kennung, von: uhrzeit(h.von), bis: uhrzeit(h.bis) })
      : t("verteilen.hindernis.einsatzOhne", { von: uhrzeit(h.von), bis: uhrzeit(h.bis) });
    case "termin": return t("verteilen.hindernis.termin", { von: uhrzeit(h.von), bis: uhrzeit(h.bis) });
    case "qualifikation": return t("verteilen.hindernis.qualifikation", {
      was: h.codes.map((c) => stand.qualifikationen.find((q) => q.code === c)?.label ?? c).join(", "),
    });
  }
}

/**
 * Das kurze Wort an der Marke — „Urlaub", „Einsatz", „ohne Kranführerschein".
 * Mit `stand` steht der Name der Qualifikation statt ihres Kürzels (Luca,
 * 08.10.2026: *„In jedem Feld steht OHNE PV DEMO"*).
 */
export function hindernisKurz(h: Hindernis, t: Tr, stand?: Pick<Verteilstand, "qualifikationen">): string {
  switch (h.art) {
    case "abwesend": return t(`verteilen.band.${h.grund}`);
    case "einsatz": case "termin": return t("verteilen.kurz.einsatz");
    case "qualifikation": return t("verteilen.kurz.qualifikation", {
      was: h.codes.map((c) => stand?.qualifikationen.find((q) => q.code === c)?.label ?? c).join(", "),
    });
  }
}

export function bandText(e: Bandeintrag, t: Tr): string {
  if (e.art === "termin") return t("verteilen.band.termin", { von: uhrzeit(e.von), bis: uhrzeit(e.bis) });
  return e.kennung
    ? t("verteilen.band.einsatz", { kennung: e.kennung, von: uhrzeit(e.von), bis: uhrzeit(e.bis) })
    : t("verteilen.band.einsatzOhne", { von: uhrzeit(e.von), bis: uhrzeit(e.bis) });
}

function dedupe(liste: Hindernis[]): Hindernis[] {
  const gesehen = new Set<string>();
  return liste.filter((h) => {
    const k = h.art === "qualifikation" ? `q:${h.codes.join(",")}` : h.art;
    if (gesehen.has(k)) return false;
    gesehen.add(k);
    return true;
  });
}

/** Was gegen diesen Menschen für diese Aufgabe spricht — nur, wenn sie ihm gehört. */
export function hindernisseAn(a: Aufgabe, stand: Verteilstand): Hindernis[] {
  if (!a.wer || !istBeweglich(a)) return [];
  const p = stand.leute.find((x) => x.id === a.wer!.id);
  return p ? hindernis(a, p, stand.band) : [];
}

/* ══════════════ Die Marke ══════════════ */

/** Der Datentyp beim Ziehen — damit ein Balken nicht als Aufgabe landet. */
export const ZIEHART = "application/x-machine-office-aufgabe";

export function Aufgabenmarke({
  aufgabe: a, stand, markiert, mitWer = false, kompakt = false, klasse, onClick, onDragStart, onDragEnd,
}: {
  aufgabe: Aufgabe;
  stand: Verteilstand;
  markiert: boolean;
  /** Am Telefon, wo keine Zeile sagt, wem sie gehört: der Name dazu. */
  mitWer?: boolean;
  /**
   * Einteilen (Luca, 08.10.2026, *„noch nicht schön"*): Art und Kennung in
   * einer Zeile — „Ausliefern KR-01" —, der Kunde steht im Block bzw. im
   * Titel. Wird es zu schmal, bricht die Kennung in die zweite Zeile.
   */
  kompakt?: boolean;
  /** Eine Klasse dazu — Einteilen markiert damit die Marke, die eben angekommen ist. */
  klasse?: string | undefined;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  onDragStart?: ((e: DragEvent<HTMLButtonElement>) => void) | undefined;
  onDragEnd?: (() => void) | undefined;
}) {
  const t = useT();
  const hindernisse = hindernisseAn(a, stand);
  const stufe = stufeVon(hindernisse);
  const zustand = a.erledigt ? "erledigt" : a.fest ? "fest" : a.wer ? "hat" : "frei";
  const titel = [
    aufgabenwort(a, t),
    `${a.kennung}${a.typ ? ` · ${a.typ}` : ""}`,
    [a.kunde, a.vorgang].filter(Boolean).join(" · "),
    [a.ort, a.ortschaft].filter(Boolean).join(", ") + (a.entfernung !== null && !a.amHof ? ` (${t("verteilen.km", { zahl: Math.round(a.entfernung) })})` : ""),
    a.art !== "vorbereitung" ? t("verteilen.um", { uhr: uhrzeit(a.zeit) }) : "",
    a.erledigt ? t("verteilen.erledigt") : a.fest ? t("verteilen.festSatz") : a.wer ? a.wer.name : t("verteilen.niemand"),
    a.zugeteiltVon ? t("verteilen.zugeteiltVon", { wer: a.zugeteiltVon }) : "",
    ...hindernisse.map((h) => hindernisText(h, stand, t)),
  ].filter(Boolean).join("\n");
  const beweglich = istBeweglich(a);
  return (
    <button
      type="button"
      className={`aufg-marke ${artKlasse(a)} ${zustand}${kompakt ? " kompakt" : ""}${markiert ? " markiert" : ""}${stufe ? ` ${stufe}` : ""}${klasse ? ` ${klasse}` : ""}`}
      title={titel}
      aria-pressed={beweglich ? markiert : undefined}
      aria-disabled={beweglich ? undefined : true}
      draggable={beweglich && !!onDragStart}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      <b>
        {a.erledigt ? <Zeichen name="haken" /> : null}
        <span className="aufg-wort">{aufgabenwort(a, t)}</span>
        {kompakt ? <span className="aufg-kennung">{a.kennung}</span> : null}
        {a.fest ? <span className="aufg-fest">{t("verteilen.fahrer")}</span> : null}
      </b>
      {kompakt ? null : <small>{[a.kennung, a.kunde].filter(Boolean).join(" · ")}</small>}
      {mitWer && !a.erledigt ? (
        <span className="aufg-wer">{a.wer ? a.wer.name : t("verteilen.niemand")}</span>
      ) : null}
      {a.tag === ZURUECK && !a.erledigt ? <em className="stopp">{t("verteilen.zurueckTag")}</em> : null}
      {stufe ? <em className={stufe}>{hindernisse.map((h) => hindernisKurz(h, t, stand)).join(" · ")}</em> : null}
    </button>
  );
}

/* ══════════════ Die Leute — im Blatt, am Brett wie am Telefon ══════════════ */

export function Leuteliste({ leute, tag, stand, aufgaben, fuer, vorschlag, waehlen }: {
  leute: Verteilperson[]; tag: string; stand: Verteilstand; aufgaben: Aufgabe[];
  /** Für diese Aufgaben wird gewählt — ihre Hindernisse stehen am Namen. */
  fuer: Aufgabe[];
  vorschlag: Verteilperson | null;
  waehlen: (p: Verteilperson) => void;
}) {
  const t = useT();
  const reihe = [...leute.filter((p) => p.stamm), ...leute.filter((p) => !p.stamm)];
  const oben = vorschlag ? [vorschlag, ...reihe.filter((p) => p.id !== vorschlag.id)] : reihe;
  const jetzt = fuer.length === 1 ? fuer[0]!.wer?.id ?? null : null;
  let trenner = false;
  return (
    <ul className="aufg-leute">
      {oben.map((p, i) => {
        const lage = lageAm(p.id, tag, stand.band, aufgaben);
        const hindernisse = dedupe(fuer.flatMap((a) => hindernis(a, p, stand.band)));
        const stufe = stufeVon(hindernisse);
        const istVorschlag = !!vorschlag && i === 0;
        const neuerAbschnitt = !p.stamm && !trenner && !istVorschlag;
        if (neuerAbschnitt) trenner = true;
        const zeilen: ReactNode[] = [];
        for (const h of hindernisse) {
          const text = hindernisText(h, stand, t);
          zeilen.push(<span key={h.art} className={h.stufe}>{text}</span>);
        }
        if (!hindernisse.length && lage.abwesend) {
          zeilen.push(<span key="ab" className="stopp">{t(`verteilen.band.${lage.abwesend.art}`)}</span>);
        }
        if (!hindernisse.some((h) => h.art === "einsatz" || h.art === "termin")) {
          for (const e of lage.einsaetze.slice(0, 2)) zeilen.push(<span key={e.von + e.art}>{bandText(e, t)}</span>);
        }
        return (
          <li key={p.id} className={neuerAbschnitt ? "aufg-leute-abschnitt" : undefined}
            data-abschnitt={neuerAbschnitt ? t("verteilen.leute.weitere") : undefined}>
            <button
              type="button"
              className={`aufg-person${stufe ? ` ${stufe}` : ""}${istVorschlag ? " vorschlag" : ""}${jetzt === p.id ? " jetzt" : ""}`}
              onClick={() => waehlen(p)}
            >
              <span className="aufg-kreis">{kuerzelVon(p)}</span>
              <span className="aufg-person-text">
                <b>
                  {p.name}
                  {istVorschlag ? <em>{t("verteilen.vorschlag")}</em> : null}
                  {jetzt === p.id ? <em className="still">{t("verteilen.hatEs")}</em> : null}
                </b>
                {zeilen.length ? <small>{zeilen}</small> : null}
              </span>
              <Last zahl={lage.aufgaben} einsaetze={lage.einsaetze.length} />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Die Tageslast als kleine Kästchen: Aufgaben voll, Einsätze umrandet. */
export function Last({ zahl, einsaetze = 0 }: { zahl: number; einsaetze?: number }) {
  const t = useT();
  const gesamt = zahl + einsaetze;
  if (!gesamt) return <span className="aufg-last leer" aria-hidden="true" />;
  return (
    <span className="aufg-last" title={t(zahl === 1 ? "verteilen.last.eins" : "verteilen.last.mehr", { zahl, einsaetze })}
      aria-label={t(zahl === 1 ? "verteilen.last.eins" : "verteilen.last.mehr", { zahl, einsaetze })}>
      {Array.from({ length: Math.min(gesamt, 5) }, (_, k) => <i key={k} className={k < zahl ? "voll" : "einsatz"} />)}
      {gesamt > 5 ? <b>+{gesamt - 5}</b> : null}
    </span>
  );
}

/* ══════════════ Zuteilen, mit dem Weg zurück ══════════════ */

type Zuletzt = { text: string; zurueck: Zuteilung[] };

/**
 * **Zuteilen mit „Rückgängig"** — dieselbe Zuteilung wie bisher
 * (`POST /aufgaben/zuteilen`: dieselben Riegel, dieselbe Spur, dieselbe
 * Meldung `zugeteilt` in der Glocke des Menschen). Nach jedem Zuteilen steht
 * neun Sekunden lang, was geschehen ist, mit dem Weg zurück.
 */
export function useVerteilung(leute: Verteilperson[]) {
  const t = useT();
  const zuteilen = useZuteilen();
  const [zuletzt, setzeZuletzt] = useState<Zuletzt | null>(null);

  useEffect(() => {
    if (!zuletzt) return;
    const uhr = window.setTimeout(() => setzeZuletzt(null), 9000);
    return () => window.clearTimeout(uhr);
  }, [zuletzt]);

  /* **Fehlt der Schein, fragt das Band nach** (D-47, Entscheidung E3:
     warnen, nicht sperren). Die API antwortet `409 aufgabe.qualifikation_*`;
     hier steht dann der Satz und „Trotzdem zuteilen", das denselben Zug mit
     der Bestätigung noch einmal schickt. */
  const [wiederholen, setzeWiederholen] = useState<(() => void) | null>(null);
  const warnung = zuteilen.error instanceof Fehler && zuteilen.error.schluessel.startsWith("aufgabe.qualifikation_");

  const geben = useCallback((liste: Aufgabe[], wer: string | null, fertig?: () => void, bestaetigt = false) => {
    const { hin, zurueck } = zuteilungenFuer(liste, wer);
    if (!hin.length) { fertig?.(); return; }
    const name = wer ? leute.find((p) => p.id === wer)?.name ?? "" : "";
    setzeWiederholen(() => () => geben(liste, wer, fertig, true));
    zuteilen.mutate(bestaetigt ? { zuteilungen: hin, bestaetigt: true } : hin, {
      onSuccess: () => {
        fertig?.();
        setzeZuletzt({
          text: wer
            ? t(hin.length === 1 ? "verteilen.gegebenEins" : "verteilen.gegebenMehr", { zahl: hin.length, name })
            : t(hin.length === 1 ? "verteilen.freiEins" : "verteilen.freiMehr", { zahl: hin.length }),
          zurueck,
        });
      },
    });
  }, [leute, t, zuteilen]);

  const vorschlaegeGeben = useCallback((liste: Zuteilung[]) => {
    if (!liste.length) return;
    zuteilen.mutate(liste, {
      onSuccess: () => setzeZuletzt({
        text: t("verteilen.vorschlaegeGegeben", { zahl: liste.length }),
        zurueck: liste.map((z) => ({ ...z, wer: null })),
      }),
    });
  }, [t, zuteilen]);

  const rueckgaengig = () => {
    if (!zuletzt) return;
    zuteilen.mutate(zuletzt.zurueck, { onSuccess: () => setzeZuletzt(null) });
  };

  const band = zuletzt || zuteilen.isError ? (
    <div className="aufg-zuletzt" role="status">
      {zuteilen.isError ? (
        <>
          <FehlerBand fehler={zuteilen.error} />
          {warnung && wiederholen ? (
            <Knopf klein onClick={() => { zuteilen.reset(); wiederholen(); }}>{t("verteilen.trotzdemZuteilen")}</Knopf>
          ) : null}
          <Knopf klein art="stumm" onClick={() => zuteilen.reset()}>{t("allgemein.schliessen")}</Knopf>
        </>
      ) : zuletzt ? (
        <>
          <span className="pkt gut" />
          <span className="aufg-zuletzt-text">{zuletzt.text}</span>
          <Knopf klein art="stumm" disabled={zuteilen.isPending} onClick={rueckgaengig}>{t("verteilen.rueckgaengig")}</Knopf>
        </>
      ) : null}
    </div>
  ) : null;

  return { geben, vorschlaegeGeben, laeuft: zuteilen.isPending, band };
}
